import type dynamicIconImports from 'lucide-react/dynamicIconImports';
import { URLS } from '@/lib/consts';
import { ServiceType, type ServiceURLs } from '../../types';

/*
 * Section Identifiers
 */
export const GETTING_STARTED_SECTION_ID = 'getting-started';
export const SUB_HEADER = 'GETTING STARTED';
export const CTA_BUTTON_TEXT = 'Request A Consultation';

/*
 * Getting Started Section Titles & Descriptions
 */
const GETTING_STARTED_TITLES = {
  [ServiceType.ROOT]: '',
  [ServiceType.BUILD]: `Starting Your Design & Build Project Is Easy...`,
  [ServiceType.RENOVATE]: `Starting Your Design & Renovation Project Is Easy...`,
} as const;

const GETTING_STARTED_DESCRIPTIONS = {
  [ServiceType.ROOT]: '',
  [ServiceType.BUILD]: `Follow these simple steps to kickstart your journey toward your dream home:`,
  [ServiceType.RENOVATE]: `Follow these simple steps to kickstart your journey toward your dream home:`,
} as const;

/*
 * Step Data Structure
 */
export type IconName = keyof typeof dynamicIconImports;

export interface GettingStartedStep {
  title: string;
  description: string;
  iconName: IconName;
}

export const GETTING_STARTED_STEPS: GettingStartedStep[] = [
  {
    title: 'Request a Consultation',
    description:
      'Connect with us to discuss your project goals and explore how we can bring your vision to life. Whether virtually, onsite, or at our office, our consultations are a chance to get to know each other and lay the foundation for success.',
    iconName: 'message-circle-more',
  },
  {
    title: 'Gain Valuable Project Insights',
    description:
      `During your consultation, we\u2019ll provide valuable insights and recommendations to help you make informed decisions about your project. With our guidance, you\u2019ll feel confident moving forward with your plans.`,
    iconName: 'lightbulb',
  },
  {
    title: 'Begin Your Project with Confidence',
    description:
      'Take the first step toward realizing your dream home with confidence. With Cornerstone Homes on your side, you can trust that your project is in capable hands.',
    iconName: 'home',
  },
];

/*
 * Type for Getting Started Section Data
 */
export interface GettingStartedConstants {
  title: string;
  description: string;
  steps: GettingStartedStep[];
}

/*
 * Generates "Getting Started" Section Data
 */
function generateGettingStartedData(
  serviceType: ServiceType,
): GettingStartedConstants {
  return {
    title: GETTING_STARTED_TITLES[serviceType],
    description: GETTING_STARTED_DESCRIPTIONS[serviceType],
    steps: GETTING_STARTED_STEPS,
  };
}

/*
 * Getting Started Section Content (Mapped by URL)
 */
const ROOT_GETTING_STARTED_SECTION_DATA = generateGettingStartedData(
  ServiceType.ROOT,
);
const BUILD_GETTING_STARTED_SECTION_DATA = generateGettingStartedData(
  ServiceType.BUILD,
);
const RENOVATE_GETTING_STARTED_SECTION_DATA = generateGettingStartedData(
  ServiceType.RENOVATE,
);

const GETTING_STARTED_SECTION_CONTENT: Record<
  ServiceURLs,
  GettingStartedConstants
> = {
  [URLS.SERVICES.ROOT]: ROOT_GETTING_STARTED_SECTION_DATA,
  [URLS.SERVICES.DESIGN_AND_BUILD]: BUILD_GETTING_STARTED_SECTION_DATA,
  [URLS.SERVICES.DESIGN_AND_RENOVATE]: RENOVATE_GETTING_STARTED_SECTION_DATA,
};

export default GETTING_STARTED_SECTION_CONTENT;
